import { type KeyboardEvent, memo, useCallback, useEffect, useReducer, useState } from 'react';
import '../../../../../css/flow/properties.less';
import { getLatest, setLatest } from '../../../sources/flow';
import translations from '../../../translations';
import type { GroupAuthorizationAPI } from '../../../types/flow';
import type { FlowIdentityProviderAPI } from '../../../types/identityProvider';
import { isNullOrEmpty, isNullOrUndefined } from '../../../utils/guard';
import ButtonDefault from '../../buttons/ButtonDefault';
import ButtonPrimary from '../../buttons/ButtonPrimary';
import Modal from '../../generic/modal/GenericModal';
import ModalBody from '../../generic/modal/ModalBody';
import ModalFooter from '../../generic/modal/ModalFooter';
import ModalHeader from '../../generic/modal/ModalHeader';
import Loader from '../../loader/Loader';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import Authorization from '../../runtime-authorization/Authorization';
import Comments from './Comments';
import Deactivation from './Deactivation';
import FlowVersionManager from './FlowVersionManager';
import Name from './Name';
import Navigation from './Navigation';
import State from './State';
import { reducer } from './reducer';
import RootFaults from './RootFaults';
import FlowTranslationNotes from './FlowTranslationNotes';

interface Props {
    flowId: string;
    setTabTitle: ({ title, key }: { title: string; key: string }) => void;
    environmentsIsOn: boolean;
    container: HTMLElement;
    close: () => void;
}

const Properties = ({ flowId, close, container, environmentsIsOn, setTabTitle }: Props) => {
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [flowConfiguration, dispatchFlowConfiguration] = useReducer(reducer, null);
    const dispatchNotification = useNotificationDispatch();

    const isFormValid = () => {
        if (isNullOrUndefined(flowConfiguration)) {
            return false;
        }
        if (flowConfiguration.developerName === '') {
            return false;
        }

        return true;
    };

    useEffect(() => {
        const handleEscKey = ({ key }: KeyboardEvent) => {
            if (key === 'Escape') {
                close();
            }
        };

        document.addEventListener(
            'keydown',
            handleEscKey as unknown as EventListenerOrEventListenerObject,
        );

        return () => {
            document.removeEventListener(
                'keydown',
                handleEscKey as unknown as EventListenerOrEventListenerObject,
            );
        };
    }, [close]);

    useEffect(() => {
        const fetchFlowConfiguration = async () => {
            const flowConfiguration = await getLatest(flowId);
            dispatchFlowConfiguration({ type: 'setFlowConfiguration', payload: flowConfiguration });
        };

        fetchFlowConfiguration().catch((error) => {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        });
    }, [flowId, dispatchNotification]);

    const saveChanges = async () => {
        if (flowConfiguration === null) {
            return;
        }

        setHasSubmitted(true);

        if (isFormValid()) {
            // If the Flow has been set to not use Users and Groups
            if (
                flowConfiguration.authorization &&
                flowConfiguration.authorization?.globalAuthenticationType !== 'SPECIFIED'
            ) {
                // Then remove the users and groups
                flowConfiguration.authorization.users = null;
                flowConfiguration.authorization.groups = null;
            }

            if (
                // If there are groups
                (flowConfiguration.authorization?.groups &&
                    // And the groups are wrong
                    (flowConfiguration.authorization.groups.some(
                        (item) => item.authenticationId === '-1',
                    ) ||
                        flowConfiguration.authorization.groups.some(
                            (item) => item.attribute === '-1',
                        ))) ||
                // If there are users
                (flowConfiguration.authorization?.users &&
                    // And the users are wrong
                    (flowConfiguration.authorization.users.some(
                        (item) => item.authenticationId === '-1',
                    ) ||
                        flowConfiguration.authorization.users.some(
                            (item) => item.attribute === '-1',
                        )))
            ) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error: 'Please select items from all of the dropdowns',
                    },
                });
                return;
            }

            if (flowConfiguration.authorization?.globalAuthenticationType === 'SPECIFIED') {
                if (
                    isNullOrEmpty(flowConfiguration.authorization.groups) &&
                    isNullOrEmpty(flowConfiguration.authorization.users)
                ) {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: {
                            error: translations.GROUPS_USERS_NONE_SELECTED_ERROR_MESSAGE,
                        },
                    });
                    return;
                }
            }

            try {
                await setLatest(flowConfiguration);
                setTabTitle({
                    title: flowConfiguration.developerName,
                    key: flowConfiguration.id.id,
                });

                close();
                dispatchNotification({
                    type: 'notifySuccess',
                    payload: {
                        message: 'Flow Properties successfully saved',
                    },
                });
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        }
    };

    const onChangeIdentityProvider = (identityProvider: FlowIdentityProviderAPI | null) =>
        dispatchFlowConfiguration({
            type: 'updateIdentityProvider',
            payload: { identityProvider },
        });

    const onChangeAuthorization = useCallback(
        (authorization: GroupAuthorizationAPI) =>
            dispatchFlowConfiguration({
                type: 'updateAuthorization',
                payload: { authorization },
            }),
        [],
    );

    return (
        <Modal container={container} onHide={close} className="flow-properties-page config-modal">
            <ModalHeader onHide={close}>
                <h4 className="modal-title">Flow Properties</h4>
            </ModalHeader>
            <ModalBody>
                {flowConfiguration === null ? (
                    <Loader message="Loading flow details" />
                ) : (
                    <>
                        <Name
                            isFormValid={isFormValid()}
                            hasSubmitted={hasSubmitted}
                            flowId={flowId}
                            developerName={flowConfiguration.developerName}
                            dispatchFlowConfiguration={dispatchFlowConfiguration}
                        />
                        <Comments
                            developerSummary={flowConfiguration.developerSummary}
                            dispatchFlowConfiguration={dispatchFlowConfiguration}
                        />
                        <Authorization
                            identityProvider={flowConfiguration.identityProvider}
                            onChangeIdentityProvider={onChangeIdentityProvider}
                            authorization={flowConfiguration.authorization}
                            onChangeAuthorization={onChangeAuthorization}
                            modalContainer={container}
                        />
                        <Navigation
                            isHistoricalNavigationEnabled={
                                flowConfiguration.enableHistoricalNavigation
                            }
                            allowJumping={flowConfiguration.allowJumping}
                            dispatchFlowConfiguration={dispatchFlowConfiguration}
                        />
                        <State
                            stateExpirationLength={flowConfiguration.stateExpirationLength}
                            idleStateExpirationLength={flowConfiguration.idleStateExpirationLength}
                            stateLockedByUserModification={
                                flowConfiguration.stateLockedByUserModification
                            }
                            dispatchFlowConfiguration={dispatchFlowConfiguration}
                        />
                        <RootFaults
                            displayRootFaults={flowConfiguration.displayRootFaults}
                            dispatchFlowConfiguration={dispatchFlowConfiguration}
                        />
                        <FlowTranslationNotes
                            translationNotes={flowConfiguration.translationNotes ?? ''}
                            dispatchFlowConfiguration={dispatchFlowConfiguration}
                        />

                        {environmentsIsOn ? (
                            <Deactivation flowId={flowId} modalContainer={container} />
                        ) : (
                            <FlowVersionManager flowId={flowId} modalContainer={container} />
                        )}
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <ButtonDefault onClick={close}>{translations.COMMON_cancel}</ButtonDefault>
                <ButtonPrimary onClick={saveChanges}>
                    {translations.GRAPH_config_panel_save}
                </ButtonPrimary>
            </ModalFooter>
        </Modal>
    );
};

export default memo(Properties);
